import StatsOverview from "@/components/stats-overview";
import { TopThreeWidget } from "@/components/top-three-widget";
import { HabitsDashboardWidget } from "@/components/habits-dashboard-widget";
import { ActiveGoalsWidget } from "@/components/active-goals-widget";
import { RecentActivitiesWidget } from "@/components/recent-activities-widget";
import { 
  GoalsOverviewWidget,
  JournalInsightsWidget,
  HabitPerformanceWidget,
  CalendarActivityWidget,
  CategoryPerformanceWidget,
  RecentActivityAnalyticsWidget,
  ProductivityInsightsWidget,
  TasksOverviewWidget,
  MeditationStatsWidget
} from "@/components/analytics-widgets";
import { ScorecardWidget } from "@/components/scorecard-widget";
import { Target, Star, Calendar, Activity, TrendingUp, BarChart3, BookOpen, Award, Trophy } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type WidgetConfig = {
  type: string;
  name: string;
  description: string;
  icon: LucideIcon;
  component: React.ComponentType<any>;
  defaultTitle: string;
  allowedSizes: ('full' | 'half' | 'quarter')[];
  defaultSize: 'full' | 'half' | 'quarter';
  defaultSettings: Record<string, any>;
  settingsSchema?: any;
};

export const WIDGET_REGISTRY: Record<string, WidgetConfig> = {
  'stats-overview': {
    type: 'stats-overview',
    name: 'Stats Overview',
    description: 'Display key metrics at a glance',
    icon: TrendingUp,
    component: StatsOverview,
    defaultTitle: 'Stats Overview',
    allowedSizes: ['full'],
    defaultSize: 'full',
    defaultSettings: {},
  },
  'top-three-tasks': {
    type: 'top-three-tasks',
    name: 'Top Tasks',
    description: 'Your starred tasks for today',
    icon: Star,
    component: TopThreeWidget,
    defaultTitle: 'Top Tasks for Today',
    allowedSizes: ['full', 'half'],
    defaultSize: 'full',
    defaultSettings: {},
  },
  'habits-widget': {
    type: 'habits-widget',
    name: 'Daily Habits',
    description: 'Track your daily habits',
    icon: Calendar,
    component: HabitsDashboardWidget,
    defaultTitle: 'Today\'s Habits',
    allowedSizes: ['full', 'half'],
    defaultSize: 'full',
    defaultSettings: { maxItems: 6 },
  },
  'active-goals': {
    type: 'active-goals',
    name: 'Active Goals',
    description: 'View your active goals',
    icon: Target,
    component: ActiveGoalsWidget,
    defaultTitle: 'Active Goals',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'half',
    defaultSettings: { maxItems: 5 },
  },
  'recent-activities': {
    type: 'recent-activities',
    name: 'Recent Activities',
    description: 'Latest activity feed',
    icon: Activity,
    component: RecentActivitiesWidget,
    defaultTitle: 'Recent Activities',
    allowedSizes: ['full', 'half'],
    defaultSize: 'half',
    defaultSettings: { maxItems: 5 },
  },
  // Analytics-specific widgets
  'analytics-goals-overview': {
    type: 'analytics-goals-overview',
    name: 'Goals Overview',
    description: 'Summary of your goals progress',
    icon: Target,
    component: GoalsOverviewWidget,
    defaultTitle: 'Goals Overview',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showProgress: true, showDetails: true },
  },
  'analytics-journal-insights': {
    type: 'analytics-journal-insights',
    name: 'Journal Insights',
    description: 'Your journaling activity and streaks',
    icon: BookOpen,
    component: JournalInsightsWidget,
    defaultTitle: 'Journal Insights',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showStreak: true, showDetails: true },
  },
  'analytics-habit-performance': {
    type: 'analytics-habit-performance',
    name: 'Habit Performance',
    description: 'Track your habit completion and streaks',
    icon: TrendingUp,
    component: HabitPerformanceWidget,
    defaultTitle: 'Habit Performance',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showStreak: true, showDetails: true },
  },
  'analytics-calendar-activity': {
    type: 'analytics-calendar-activity',
    name: 'Calendar Activity',
    description: 'Overview of your calendar events',
    icon: Calendar,
    component: CalendarActivityWidget,
    defaultTitle: 'Calendar Activity',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showDetails: true },
  },
  'analytics-category-performance': {
    type: 'analytics-category-performance',
    name: 'Category Performance',
    description: 'Goal progress by category',
    icon: BarChart3,
    component: CategoryPerformanceWidget,
    defaultTitle: 'Category Performance',
    allowedSizes: ['full', 'half'],
    defaultSize: 'half',
    defaultSettings: { maxItems: 5 },
  },
  'analytics-recent-activity': {
    type: 'analytics-recent-activity',
    name: 'Recent Activity',
    description: 'Your latest actions and updates',
    icon: Activity,
    component: RecentActivityAnalyticsWidget,
    defaultTitle: 'Recent Activity',
    allowedSizes: ['full', 'half'],
    defaultSize: 'half',
    defaultSettings: { maxItems: 8 },
  },
  'analytics-productivity-insights': {
    type: 'analytics-productivity-insights',
    name: 'Productivity Insights',
    description: 'Key productivity metrics at a glance',
    icon: Award,
    component: ProductivityInsightsWidget,
    defaultTitle: 'Productivity Insights',
    allowedSizes: ['full'],
    defaultSize: 'full',
    defaultSettings: { metric: 'all' },
  },
  'analytics-tasks-overview': {
    type: 'analytics-tasks-overview',
    name: 'Tasks Overview',
    description: 'Summary of your task progress',
    icon: BarChart3,
    component: TasksOverviewWidget,
    defaultTitle: 'Tasks Overview',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showDetails: true },
  },
  'analytics-meditation-stats': {
    type: 'analytics-meditation-stats',
    name: 'Meditation Stats',
    description: 'Track your meditation practice',
    icon: Award,
    component: MeditationStatsWidget,
    defaultTitle: 'Meditation Stats',
    allowedSizes: ['full', 'half', 'quarter'],
    defaultSize: 'quarter',
    defaultSettings: { showDetails: true },
  },
  'scorecard': {
    type: 'scorecard',
    name: 'AI Scorecard',
    description: 'AI-powered performance score across all areas with trend history',
    icon: Trophy,
    component: ScorecardWidget,
    defaultTitle: 'My Scorecard',
    allowedSizes: ['full', 'half'],
    defaultSize: 'half',
    defaultSettings: {
      period: 'weekly',
      rangeType: 'rolling',
      showTrend: true,
      history: [],
    },
  },
};
